import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { ItemCreateReturnLitigationModal } from '@/types/modal';
import { LitigationType } from '@/types/omsStatus';
import { useCreateItemReturnLitigation, useGetItemCreateReturnLitigation } from '@/hooks/item';
import { ErrorModal, StandardModal, NoFooterModal, LoadingModal } from '@/components/molecules/common/Modal';
import { Dropdown, Input } from '@/components/molecules/common/Form';
import { Skeleton } from '@mui/material';
import styles from '../Modal.module.scss';
import TranslationWithTicketRef from './TicketNumberLink';
import ReturnLitigationDone from './ReturnLitigationDone';
import LitigationContent from './LitigationContent';
import { TimelineType } from '@/types/item';

const View: React.FC<{ modal: ItemCreateReturnLitigationModal }> = ({ modal }) => {
  const { t } = useTranslation();

  const { item, onClose, litigationType } = modal;

  const _t = (key: string) => {
    let type = 'create_return_litigation';
    switch (litigationType) {
      case LitigationType.POSTAL_SELLER:
        type = 'create_seller_portal_litigation';
        break;
      case LitigationType.POSTAL_BUYER:
        type = 'create_buyer_portal_litigation';
        break;
      case LitigationType.REPLACEMENT_ZENDESK_TICKET:
        type = 'replace_zendesk_ticket';
        break;
      default:
        break;
    }
    return t(key.replace('#type#', type));
  };

  const [isDeliveredStatus, setIsDeliveredStatus] = useState(false.toString());
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');
  const [shouldSubmitNewTicket, setShouldSubmitNewTicket] = useState(false);

  const isPortalLitigation = [
    LitigationType.POSTAL_SELLER,
    LitigationType.POSTAL_BUYER,
    LitigationType.REPLACEMENT_ZENDESK_TICKET,
  ].includes(litigationType);

  const detailR = useGetItemCreateReturnLitigation({
    productId: Number(item.productId),
    litigation: isPortalLitigation ? 'postal' : '',
    isReplacementTicket: litigationType === LitigationType.REPLACEMENT_ZENDESK_TICKET,
    clientType:
      litigationType === LitigationType.REPLACEMENT_ZENDESK_TICKET
        ? ''
        : litigationType === LitigationType.POSTAL_SELLER
          ? TimelineType.SELLER
          : TimelineType.BUYER,
  });
  const submitNewTicketR = useCreateItemReturnLitigation();

  let submittable = true;
  let submitLabel = t('common.submit');
  let closeLabel = undefined;
  let ignoreSubmitLabel = false;

  const isDeliveredStatuses = [
    {
      label: t('common.yes'),
      value: true.toString(),
    },
    {
      label: t('common.no'),
      value: false.toString(),
    },
  ];

  const onSubmit = () => {
    if (shouldSubmitNewTicket) {
      onSubmitNewTicket();
    } else {
      setShouldSubmitNewTicket(true);
    }
  };

  useEffect(() => {
    if (detailR.kind === 'Loaded') {
      const prefillData = detailR?.data?.prefillData;
      if (prefillData) {
        setIsDeliveredStatus(prefillData.itemReceived?.toString());
        setReason(prefillData.reason?.toString());
      }
    }
  }, [detailR.kind]);

  if (submitNewTicketR.kind === 'Completed') {
    const { zendeskTicketLink, zendeskTicketId } = submitNewTicketR.data;

    return (
      <StandardModal
        title={t('oms_return_status.create_return_litigation.success.title')}
        onClose={onClose}
        onSubmit={onSubmit}
        submittable={false}
        modalName="create_return_litigation.success"
        body={
          <div className={styles.containerGap}>
            <div className={styles.text1rem}>{`${t('common.item')} #${item.productId}`}</div>
            <div className={styles.text1rem}>
              <TranslationWithTicketRef
                link={zendeskTicketLink || ''}
                ticketId={zendeskTicketId || ''}
                translateKey="oms_return_status.create_return_litigation.success.description"
              />
            </div>
          </div>
        }
        containerStyle={{ maxWidth: 650 }}
        closeLabel={t('common.close')}
        ignoreSubmitLabel={true}
      />
    );
  }

  if (submitNewTicketR.kind === 'Loading') {
    return (
      <NoFooterModal
        title={_t('oms_return_status.#type#.title')}
        body={
          <>
            <Skeleton
              height={20}
              variant="rounded"
            />
            <div className={styles.updateItemAttributeWait}>
              {_t('oms_return_status.#type#.not_exist_ticket.waiting')}
            </div>
          </>
        }
        onClose={onClose}
        modalName="item_create_return_litigation"
      />
    );
  }

  if (submitNewTicketR.kind === 'Error') {
    return (
      <ErrorModal
        title={_t('oms_return_status.#type#.title')}
        error={submitNewTicketR.error}
        onClose={onClose}
        containerStyle={{
          width: 400,
        }}
      />
    );
  }

  if (detailR.kind === 'Called') {
    return (
      <LoadingModal
        title={_t('oms_return_status.#type#.title')}
        onClose={onClose}
      />
    );
  }

  if (detailR.kind === 'Error') {
    return (
      <ErrorModal
        title={_t('oms_return_status.#type#.title')}
        error={detailR.error}
        onClose={onClose}
      />
    );
  }

  const {
    zendeskTicketId,
    zendeskTicketLink,
    zendeskTicketStatus,
    litigationDecision,
    canAccessReturnForm,
    itemReceived,
    itemNotReceived,
    prefillData,
  } = detailR.data;

  const onSubmitNewTicket = () => {
    submitNewTicketR.mutate({
      description: description,
      isReplacementTicket: litigationType === LitigationType.REPLACEMENT_ZENDESK_TICKET,
      itemReceived: isDeliveredStatus.toLowerCase() === 'true',
      reason: reason,
      productId: item.productId,
      litigation: isPortalLitigation ? 'postal' : '',
      clientType: (() => {
        if (litigationType === LitigationType.REPLACEMENT_ZENDESK_TICKET) {
          return prefillData?.isSellerWorkflow ? TimelineType.SELLER : TimelineType.BUYER;
        }

        return litigationType === LitigationType.POSTAL_SELLER ? TimelineType.SELLER : TimelineType.BUYER;
      })(),
    });
  };

  let body = <div></div>;

  if (!zendeskTicketId || shouldSubmitNewTicket) {
    if (!canAccessReturnForm) {
      return (
        <ErrorModal
          title={_t('oms_return_status.#type#.title')}
          message={_t('oms_return_status.#type#.not_exist_ticket.error')}
          onClose={onClose}
        />
      );
    }
    if (!shouldSubmitNewTicket) {
      setShouldSubmitNewTicket(true);
    }

    const itemReceivedReasons = itemReceived
      ? itemReceived.map(({ value, displayText, displayWarning }) => ({
          label: displayText ?? '',
          value: value ?? '',
          displayWarning: displayWarning ?? '',
        }))
      : [];
    const itemNotReceivedReasons = itemNotReceived
      ? itemNotReceived.map(({ value, displayText, displayWarning }) => ({
          label: displayText ?? '',
          value: value ?? '',
          displayWarning: displayWarning ?? '',
        }))
      : [];

    if (!itemNotReceivedReasons.length) {
      if (isDeliveredStatus !== true.toString()) {
        setIsDeliveredStatus(true.toString());
      }
    }

    const reasons = {
      true: itemReceivedReasons,
      // false is default
      false: itemNotReceivedReasons,
    }; // if buyer received item or not
    const isExistBothItemReasons = !!itemReceivedReasons.length && !!itemNotReceivedReasons.length;

    const selectedReason = (
      isDeliveredStatus.toLowerCase() === 'true' ? itemReceivedReasons : itemNotReceivedReasons
    ).find((r) => r.value === reason);

    submittable = !!description && !!isDeliveredStatus && !!reason;

    body = (
      <div className={styles.containerGap}>
        <div className={styles.text1rem}>{`${t('common.item')} #${item.productId}`}</div>
        <div className={clsx([styles.text1rem, styles.textGrey, styles.textPreline])}>
          {_t('oms_return_status.#type#.not_exist_ticket.description')}
        </div>
        {isExistBothItemReasons && (
          <Dropdown
            required={true}
            disabled={false}
            label={t('oms_return_status.create_return_litigation.not_exist_ticket.buyer_receive_the_item')}
            value={isDeliveredStatus}
            onSelect={setIsDeliveredStatus}
            items={isDeliveredStatuses}
          />
        )}
        <Dropdown
          required={true}
          disabled={false}
          label={t('oms_return_status.create_return_litigation.not_exist_ticket.issue')}
          value={reason}
          onSelect={setReason}
          items={isDeliveredStatus.toLowerCase() === 'true' ? reasons.true : reasons.false}
        />
        <Input
          required={true}
          disabled={false}
          label={t('oms_return_status.create_return_litigation.not_exist_ticket.issue_description')}
          value={description}
          multiline={true}
          rows={4}
          onChange={setDescription}
        />
        {selectedReason?.displayWarning && litigationType === LitigationType.RETURN && (
          <div className={clsx([styles.text1rem, styles.textRed])}>
            {selectedReason.displayWarning}
          </div>
        )}
      </div>
    );
  } else {
    const zenDeskLinkInfo = {
      link: zendeskTicketLink || '',
      ticketId: zendeskTicketId || '',
    };

    if (canAccessReturnForm) {
      ignoreSubmitLabel = false;
      submitLabel = t('common.yes');
      closeLabel = t('common.no');
    } else {
      submitLabel = '';
      closeLabel = t('common.ok');
      ignoreSubmitLabel = true;
    }

    switch (litigationType) {
      case LitigationType.POSTAL_SELLER:
        body = (
          <LitigationContent
            productId={item.productId}
            descriptionContent={
              <TranslationWithTicketRef
                {...zenDeskLinkInfo}
                translateKey="oms_return_status.create_seller_portal_litigation.exist_ticket.not_closed.oms_not_agree_disagree.description"
              />
            }
          />
        );
        break;
      case LitigationType.POSTAL_BUYER:
        body = (
          <LitigationContent
            productId={item.productId}
            descriptionContent={
              <TranslationWithTicketRef
                {...zenDeskLinkInfo}
                translateKey="oms_return_status.create_buyer_portal_litigation.exist_ticket.not_closed.oms_not_agree_disagree.description"
              />
            }
          />
        );
        break;
      default:
        // Return litigation
        body = (
          <ReturnLitigationDone
            canAccessReturnForm={!!canAccessReturnForm}
            zenDeskLinkInfo={zenDeskLinkInfo}
            litigationDecision={litigationDecision || ''}
            zendeskTicketStatus={zendeskTicketStatus || ''}
            productId={item.productId}
          />
        );
    }
  }

  return (
    <StandardModal
      title={_t('oms_return_status.#type#.title')}
      onClose={onClose}
      onSubmit={onSubmit}
      submittable={submittable}
      body={body}
      containerStyle={{ maxWidth: 650 }}
      submitLabel={submitLabel?.toUpperCase()}
      closeLabel={closeLabel?.toUpperCase()}
      ignoreSubmitLabel={ignoreSubmitLabel}
      modalName="create_return_litigation"
    />
  );
};

export default View;
